<script setup lang="ts">
import { GripVertical, Pencil, Plus, Trash2, TriangleAlert } from 'lucide-vue-next'
import { VueDraggableNext as draggable } from 'vue-draggable-next'

export interface CardListItem {
  id: string
  title: string
  subtitle?: string
  image?: string
  warning?: string
  hidden?: boolean
}

const { t } = useI18n()

const props = defineProps<{
  items: CardListItem[]
  addLabel: string
  canAdd?: boolean
  activeId?: string
}>()

const emit = defineEmits<{
  'update:order': [ids: string[]]
  'update:visible': [id: string, isVisible: boolean]
  edit: [id: string]
  remove: [id: string]
  add: []
}>()

const rows = computed({
  get: () => props.items,
  set: (next: CardListItem[]) =>
    emit(
      'update:order',
      next.map((item) => item.id)
    )
})
</script>

<template>
  <div class="flex flex-col gap-2">
    <draggable v-model="rows" handle=".drag-handle" :animation="200" class="flex flex-col gap-2">
      <TransitionGroup
        enter-active-class="motion-safe:animate-card-pop"
        leave-active-class="motion-safe:animate-card-collapse overflow-hidden"
      >
        <div
          v-for="item in rows"
          :key="item.id"
          class="border-border bg-background shadow-surface flex items-center gap-3 rounded-xl border p-3"
          :class="[
            item.warning ? 'border-amber-300 bg-amber-50' : '',
            item.id === activeId ? 'border-primary ring-primary ring-1' : ''
          ]"
        >
          <GripVertical class="drag-handle text-foreground-disabled size-4 shrink-0 cursor-grab" />
          <img
            v-if="item.image"
            :src="item.image"
            alt=""
            class="aspect-4/3 w-12 shrink-0 rounded-md object-cover"
            :class="item.hidden && 'opacity-50'"
          />
          <span v-else class="bg-muted aspect-4/3 w-12 shrink-0 rounded-md" :class="item.hidden && 'opacity-50'" />
          <span class="flex min-w-0 flex-col" :class="item.hidden && 'opacity-50'">
            <span class="text-foreground truncate text-sm font-medium">{{ item.title }}</span>
            <span v-if="item.warning" class="flex items-center gap-1 text-xs text-amber-700">
              <TriangleAlert class="size-3 shrink-0" />
              {{ item.warning }}
            </span>
            <span v-else-if="item.subtitle" class="text-muted-foreground truncate text-xs">{{ item.subtitle }}</span>
          </span>
          <span class="ml-auto flex shrink-0 items-center gap-1">
            <Button
              v-if="!item.warning"
              variant="ghost"
              size="icon-sm"
              :aria-label="t('pages.loyaltySupports.webapp.editor.cards.edit')"
              @click="emit('edit', item.id)"
            >
              <Pencil />
            </Button>
            <Button
              variant="ghost"
              size="icon-sm"
              :aria-label="t('pages.loyaltySupports.webapp.editor.cards.remove')"
              @click="emit('remove', item.id)"
            >
              <Trash2 />
            </Button>
            <Switch
              v-if="item.hidden !== undefined"
              :model-value="!item.hidden"
              :aria-label="t('pages.loyaltySupports.webapp.editor.cards.showInApp')"
              @update:model-value="emit('update:visible', item.id, $event)"
            />
          </span>
        </div>
      </TransitionGroup>
    </draggable>

    <Button
      variant="outline"
      class="h-auto justify-center rounded-xl border-dashed py-3"
      :disabled="canAdd === false"
      @click="emit('add')"
    >
      <Plus />
      {{ addLabel }}
    </Button>
  </div>
</template>
